import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { Product } from '../../types.ts';
import SocialShareButtons from '../common/SocialShareButtons.tsx';
import {
  ShoppingBag,
  MessageCircle,
  Phone,
  Share2,
  Check,
  CheckCircle2,
  ArrowLeft,
  Tag,
  Clock,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Play,
  Film,
  Eye,
  Info,
  Calendar,
  Layers,
  Award,
  Compass,
  BookOpen,
  ExternalLink,
  FileText,
} from 'lucide-react';
import {
  getYouTubeEmbedUrl,
  extractIframeSrc,
  isIframeEmbed,
  getYouTubeId,
} from '../../utils/videoUtils.ts';

interface ProductDetailPageProps {
  slugOrId: string;
}

export default function ProductDetailPage({ slugOrId }: ProductDetailPageProps) {
  const { products, articles, navigate, showToast, settings } = useApp();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeMediaTab, setActiveMediaTab] = useState<'image' | 'video'>('image');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const primaryColor = settings?.theme?.primaryColor || '#059669';
  const siteName = settings?.siteName || 'Nusantara Sinergi';

  useEffect(() => {
    async function loadProductData() {
      setLoading(true);
      try {
        const res = await fetch(`/api/products/${slugOrId}`);
        const data = await res.json();
        if (data.success && data.data) {
          setProduct(data.data);
          setSelectedImage(data.data.image || '');
          if (data.data.videoUrl) {
            setActiveMediaTab('image');
          }
        } else {
          // Fallback to local state if offline or syncing
          const local = products.find((p) => p.slug === slugOrId || p.id === slugOrId);
          if (local) {
            setProduct(local);
            setSelectedImage(local.image || '');
          }
        }
      } catch (err) {
        console.error('Failed to fetch product:', err);
        const local = products.find((p) => p.slug === slugOrId || p.id === slugOrId);
        if (local) {
          setProduct(local);
          setSelectedImage(local.image || '');
        }
      } finally {
        setLoading(false);
      }
    }
    loadProductData();
  }, [slugOrId, products]);

  // Update dynamic document title for SEO
  useEffect(() => {
    if (product) {
      document.title = `${product.title} | ${siteName}`;
    }
  }, [product, siteName]);

  const formatRupiah = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val);
  };

  const getWhatsAppLink = (prod: Product) => {
    const waNumber = (settings?.contact?.whatsappNumber || '6281234567890').replace(/[^0-9]/g, '');
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const priceText = prod.hidePrice ? '' : ` seharga ${formatRupiah(prod.price)}`;
    const defaultMsg = `Halo Admin ${siteName}, saya tertarik untuk memesan/konsultasi mengenai paket "${prod.title}"${priceText}.\nLink produk: ${currentUrl}\n\nMohon info ketersediaan jadwal & detail penawarannya. Terima kasih!`;
    const message = prod.whatsappMessage || defaultMsg;
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
  };

  const handleShare = (platform: 'whatsapp' | 'twitter' | 'facebook' | 'copy') => {
    const url = window.location.href;
    const title = product?.title || '';

    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' - ' + url)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setCopied(true);
      showToast('Link produk berhasil disalin! Siap dibagikan ke WhatsApp atau media sosial.', 'success');
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);

  const galleryImages = [
    product?.image,
    ...(product?.gallery || []),
  ].filter(Boolean) as string[];

  const activePhotoIndex = Math.max(0, galleryImages.findIndex((img) => img === selectedImage));

  const handleNextPhoto = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (galleryImages.length <= 1) return;
    const nextIdx = (activePhotoIndex + 1) % galleryImages.length;
    setSelectedImage(galleryImages[nextIdx]);
  };

  const handlePrevPhoto = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (galleryImages.length <= 1) return;
    const prevIdx = (activePhotoIndex - 1 + galleryImages.length) % galleryImages.length;
    setSelectedImage(galleryImages[prevIdx]);
  };

  const handlePhotoTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handlePhotoTouchEnd = (e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        handleNextPhoto();
      } else {
        handlePrevPhoto();
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 text-sm font-semibold mt-4">Loading service details & specifications...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShoppingBag className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800">Service Package Not Found</h1>
        <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
          The service or solution package you requested may have been relocated or updated.
        </p>
        <button
          onClick={() => navigate('products')}
          className="mt-6 px-6 py-2.5 rounded-xl text-white font-bold text-sm shadow-md hover:opacity-90 transition-all inline-flex items-center gap-2"
          style={{ backgroundColor: primaryColor }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Services Catalog</span>
        </button>
      </div>
    );
  }

  const relatedProducts = products
    .filter((p) => p.id !== product.id && (p.category === product.category || !product.category))
    .slice(0, 3);

  const discountPercent =
    !product.hidePrice && product.originalPrice && product.originalPrice > product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;

  return (
    <div id="product-detail-dedicated-page" className="py-6 sm:py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Breadcrumb Navigation & Top Action Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
          <nav className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap">
            <button
              onClick={() => navigate('home')}
              className="hover:text-slate-900 transition-colors font-medium"
            >
              Home
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <button
              onClick={() => navigate('products')}
              className="hover:text-slate-900 transition-colors font-medium"
            >
              Services & Solutions
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
              {product.category || 'Service'}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0 hidden sm:inline" />
            <span className="font-bold text-slate-900 truncate max-w-xs hidden sm:inline">
              {product.title}
            </span>
          </nav>

          {/* Share & Direct Link Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => handleShare('copy')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all flex items-center gap-1.5 ${
                copied
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-2xs'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copied ? 'Link Copied!' : 'Share Link'}</span>
            </button>

            <button
              type="button"
              onClick={() => handleShare('whatsapp')}
              className="p-1.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-all"
              title="Share on WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => navigate('products')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-all flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>All Packages</span>
            </button>
          </div>
        </div>

        {/* Main Product Presentation Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          
          {/* LEFT COLUMN: Visual Media & Video Showcase (5 cols) */}
          <div className="lg:col-span-6 space-y-4">
            {/* Media Tabs if Video is Present */}
            {product.videoUrl && (
              <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl w-fit">
                <button
                  type="button"
                  onClick={() => setActiveMediaTab('image')}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeMediaTab === 'image'
                      ? 'bg-white text-slate-900 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Tag className="w-3.5 h-3.5" />
                  <span>Foto & Galeri ({galleryImages.length})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveMediaTab('video')}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeMediaTab === 'video'
                      ? 'bg-purple-600 text-white shadow-xs'
                      : 'text-purple-700 hover:bg-purple-50'
                  }`}
                >
                  <Film className="w-3.5 h-3.5" />
                  <span>Video Teaser</span>
                </button>
              </div>
            )}

            {/* Main Media Container */}
            <div
              className="bg-slate-950 rounded-2xl overflow-hidden border border-slate-200/80 shadow-md relative group aspect-4/3 flex items-center justify-center select-none touch-pan-y"
              onTouchStart={activeMediaTab === 'image' ? handlePhotoTouchStart : undefined}
              onTouchEnd={activeMediaTab === 'image' ? handlePhotoTouchEnd : undefined}
            >
              {activeMediaTab === 'video' && product.videoUrl ? (
                isIframeEmbed(product.videoUrl) ? (
                  <iframe
                    src={extractIframeSrc(product.videoUrl)}
                    title={product.title}
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : product.videoType === 'youtube' || getYouTubeId(product.videoUrl) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(product.videoUrl, true)}
                    title={product.title}
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={product.videoUrl}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                )
              ) : (
                <div className="relative w-full h-full bg-slate-100 flex items-center justify-center">
                  <img
                    src={selectedImage || product.image || 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1200&q=80'}
                    alt={product.title}
                    className="w-full h-full object-cover select-none transition-all duration-300"
                  />
                  {product.badge && (
                    <div
                      className="absolute top-4 left-4 px-3 py-1 rounded-lg text-xs font-extrabold uppercase tracking-wide text-white shadow-lg flex items-center gap-1.5 z-10"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Tag className="w-3.5 h-3.5 text-amber-300" />
                      <span>{product.badge}</span>
                    </div>
                  )}
                  {discountPercent > 0 && (
                    <div className="absolute top-4 right-4 bg-rose-600 text-white px-2.5 py-1 rounded-lg text-xs font-extrabold shadow-md z-10">
                      HEMAT {discountPercent}%
                    </div>
                  )}

                  {/* Navigation Arrows for Multiple Photos */}
                  {galleryImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handlePrevPhoto}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center backdrop-blur-xs transition-transform hover:scale-105 opacity-90 z-20 cursor-pointer"
                        aria-label="Foto sebelumnya"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleNextPhoto}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center backdrop-blur-xs transition-transform hover:scale-105 opacity-90 z-20 cursor-pointer"
                        aria-label="Foto selanjutnya"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-[11px] font-semibold px-3 py-1 rounded-full backdrop-blur-xs z-20 flex items-center gap-1.5">
                        <span>{activePhotoIndex + 1} / {galleryImages.length}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Gallery Thumbnails List */}
            {galleryImages.length > 1 && activeMediaTab === 'image' && (
              <div className="space-y-1.5">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Galeri Dokumentasi & Detail Visual
                </span>
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedImage(img)}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                        selectedImage === img
                          ? 'border-emerald-600 ring-2 ring-emerald-500/20 scale-95 shadow-sm'
                          : 'border-slate-200 hover:border-slate-400 opacity-75 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Trust Badges & Service Guarantee */}
            {product.showTrustBadges !== false && (
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(product.trustBadges && product.trustBadges.length > 0 ? product.trustBadges : [
                  { title: '100% Terverifikasi', subtitle: 'Legalitas & EO Berizin Resmi' },
                  { title: 'Instruktur BNSP', subtitle: 'Pemandu Bersertifikasi' }
                ]).map((badge, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <div className="p-2 bg-emerald-100 text-emerald-800 rounded-xl shrink-0">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-900">{badge.title}</h4>
                      <p className="text-[10px] text-slate-500">{badge.subtitle}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Product Specifications & WhatsApp Direct Conversion (6 cols) */}
          <div className="lg:col-span-6 space-y-6">
            
            {/* Title & Category Header */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                  {product.category || 'Layanan Korporat'}
                </span>
                {product.availabilityStatus !== '' && (
                  <>
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-xs text-emerald-700 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{product.availabilityStatus || 'Siap Dipesan / Tersedia'}</span>
                    </span>
                  </>
                )}
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-snug">
                {product.title}
              </h1>
            </div>

            {/* Price Box with Highlights */}
            <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100/80 rounded-2xl border border-slate-200 shadow-2xs space-y-3">
              {product.hidePrice ? (
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                    {product.priceLabel || 'Estimasi Biaya & Penawaran Paket:'}
                  </span>
                  <div className="flex items-baseline gap-3">
                    <span
                      className="text-2xl sm:text-3xl font-black tracking-tight"
                      style={{ color: primaryColor }}
                    >
                      {product.priceCustomLabel || 'Hubungi Kami / Konsultasi'}
                    </span>
                  </div>
                  {product.priceNote !== '' && (
                    <p className="text-xs text-slate-500 pt-2.5 mt-2 border-t border-slate-200/80">
                      {product.priceNote || '*Biaya penawaran resmi (RAB) akan disesuaikan secara transparan & fleksibel berdasarkan jumlah peserta, lokasi kegiatan, durasi acara, dan paket fasilitas pilihan Anda.'}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                        {product.priceLabel || 'Biaya Investasi / Harga Paket:'}
                      </span>
                      <div className="flex items-baseline gap-3">
                        <span
                          className="text-3xl sm:text-4xl font-black tracking-tight"
                          style={{ color: primaryColor }}
                        >
                          {formatRupiah(product.price)}
                        </span>
                        {product.originalPrice && product.originalPrice > product.price && (
                          <span className="text-sm font-semibold text-slate-400 line-through">
                            {formatRupiah(product.originalPrice)}
                          </span>
                        )}
                      </div>
                    </div>

                    {discountPercent > 0 && (
                      <span className="px-3 py-1 bg-rose-100 text-rose-800 text-xs font-black rounded-lg w-fit">
                        Hemat {formatRupiah(product.originalPrice! - product.price)}
                      </span>
                    )}
                  </div>

                  {product.priceNote !== '' && (
                    <p className="text-xs text-slate-500 pt-2 border-t border-slate-200/80">
                      {product.priceNote || '*Harga sudah mencakup perlengkapan standar, pendampingan instruktur, dan koordinasi lapangan.'}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Fast WhatsApp 1-Click Action Buttons */}
            <div className="space-y-3">
              <a
                id="dedicated-product-order-wa-btn"
                href={getWhatsAppLink(product)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3.5 px-6 rounded-xl text-white font-extrabold text-sm sm:text-base shadow-md hover:shadow-lg hover:opacity-95 active:scale-[0.99] transition-all flex items-center justify-center gap-2.5 text-center cursor-pointer"
                style={{ backgroundColor: primaryColor }}
              >
                <MessageCircle className="w-5 h-5 fill-white/20" />
                <span>{product.orderButtonText || 'Pesan & Konsultasi via WhatsApp'}</span>
              </a>

              <div className="grid grid-cols-2 gap-3">
                {product.showPhoneButton !== false && (
                  <a
                    href={`tel:${(product.customPhone || settings?.contact?.phone || '+6281234567890').replace(/[^0-9+]/g, '')}`}
                    className="py-2.5 px-4 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 hover:bg-slate-50 transition-all flex items-center justify-center gap-1.5 shadow-2xs"
                  >
                    <Phone className="w-3.5 h-3.5 text-slate-600" />
                    <span>{product.phoneButtonText || 'Telepon Kantor'}</span>
                  </a>
                )}

                {product.showShareButton !== false && (
                  <button
                    type="button"
                    onClick={() => handleShare('copy')}
                    className={`py-2.5 px-4 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 hover:bg-slate-50 transition-all flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer ${
                      product.showPhoneButton === false ? 'col-span-2' : ''
                    }`}
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5 text-slate-600" />}
                    <span>{copied ? 'Tersalin' : 'Bagikan Brosur'}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Key Features & Facilities Included */}
            {product.showFeatures !== false && product.features && product.features.length > 0 && (
              <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                  <Layers className="w-4 h-4 text-emerald-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    {product.featuresTitle || 'Fasilitas & Keunggulan Paket Ini:'}
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                  {product.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-slate-700 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inclusions & Exclusions */}
            {product.showInclusionsExclusions && ((product.inclusions && product.inclusions.length > 0) || (product.exclusions && product.exclusions.length > 0)) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {product.inclusions && product.inclusions.length > 0 && (
                  <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200/80 space-y-2">
                    <h4 className="text-xs font-bold text-emerald-900 uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span>{product.inclusionsTitle || 'Termasuk Dalam Paket (Inclusions)'}</span>
                    </h4>
                    <ul className="space-y-1.5 pt-1">
                      {product.inclusions.map((item, idx) => (
                        <li key={idx} className="text-xs text-emerald-950 flex items-start gap-1.5 font-medium">
                          <span className="text-emerald-600 font-bold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {product.exclusions && product.exclusions.length > 0 && (
                  <div className="p-4 bg-rose-50/60 rounded-2xl border border-rose-200/80 space-y-2">
                    <h4 className="text-xs font-bold text-rose-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Tag className="w-4 h-4 text-rose-600" />
                      <span>{product.exclusionsTitle || 'Tidak Termasuk Dalam Paket (Exclusions)'}</span>
                    </h4>
                    <ul className="space-y-1.5 pt-1">
                      {product.exclusions.map((item, idx) => (
                        <li key={idx} className="text-xs text-rose-950 flex items-start gap-1.5 font-medium">
                          <span className="text-rose-600 font-bold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Full Product Description */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                <Info className="w-4 h-4 text-slate-500" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  {product.descriptionTitle || 'Deskripsi Lengkap & Rincian Kegiatan'}
                </h3>
              </div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-line space-y-3">
                {product.description}
              </div>
            </div>

            {/* Rundown / Itinerary / Jadwal Kegiatan */}
            {product.showItinerary && product.itinerary && product.itinerary.length > 0 && (
              <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    {product.itineraryTitle || 'Rundown & Jadwal Kegiatan'}
                  </h3>
                </div>
                <div className="space-y-3 pt-1">
                  {product.itinerary.map((item, idx) => (
                    <div key={idx} className="flex gap-3 text-xs">
                      <span className="px-2.5 py-1 bg-purple-50 text-purple-700 font-extrabold rounded-lg shrink-0 h-fit text-[11px]">
                        {item.timeOrDay}
                      </span>
                      <div>
                        <h4 className="font-bold text-slate-900">{item.title}</h4>
                        {item.description && (
                          <p className="text-slate-500 mt-0.5 leading-relaxed">{item.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Syarat & Ketentuan Pemesanan */}
            {product.showTerms && product.terms && product.terms.length > 0 && (
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-200/80 pb-2.5">
                  <FileText className="w-4 h-4 text-slate-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    {product.termsTitle || 'Syarat & Ketentuan Pemesanan'}
                  </h3>
                </div>
                <ul className="space-y-2 pt-1">
                  {product.terms.map((term, idx) => (
                    <li key={idx} className="text-xs text-slate-700 flex items-start gap-2">
                      <span className="text-slate-400 font-bold shrink-0">{idx + 1}.</span>
                      <span>{term}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* FAQ Khusus Produk */}
            {product.showFaqs && product.faqs && product.faqs.length > 0 && (
              <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                  <Info className="w-4 h-4 text-blue-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    {product.faqsTitle || 'Pertanyaan Sering Diajukan (FAQ)'}
                  </h3>
                </div>
                <div className="space-y-3 pt-1">
                  {product.faqs.map((faq, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-xl space-y-1">
                      <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                        <span className="text-emerald-600">Q:</span>
                        <span>{faq.question}</span>
                      </h4>
                      <p className="text-xs text-slate-600 pl-4 leading-relaxed">
                        <span className="text-slate-400 font-semibold mr-1">A:</span>
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Proposal / Group Inquiries Box */}
            {product.showCtaBox !== false && (
              <div className="p-5 bg-slate-900 text-white rounded-2xl space-y-3 shadow-md">
                <div className="flex items-center gap-2">
                  <Compass className="w-4 h-4 text-amber-400" />
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-amber-400">
                    {product.ctaBoxBadge || 'Ingin Paket Khusus / Penawaran Resmi Corporate?'}
                  </h4>
                </div>
                <h3 className="text-base font-bold text-white">
                  {product.ctaBoxTitle || 'Dapatkan Proposal & Surat Penawaran Resmi'}
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {product.ctaBoxSubtitle || 'Kami melayani penyusunan proposal resmi (RAB), penyesuaian jumlah peserta mulai dari 20 hingga 1.000+ orang, serta opsi survei lokasi gratis bersama tim EO.'}
                </p>
                <a
                  href={
                    product.ctaBoxWhatsappMessage
                      ? `https://wa.me/${(settings?.contact?.whatsappNumber || '6281234567890').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(product.ctaBoxWhatsappMessage)}`
                      : getWhatsAppLink(product)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-extrabold text-emerald-400 hover:text-emerald-300 underline cursor-pointer"
                >
                  <span>{product.ctaBoxButtonText || 'Minta Surat Penawaran & Proposal PDF via WA →'}</span>
                </a>
              </div>
            )}

            {/* Social Share Box */}
            <SocialShareButtons
              title={product.title}
              summary={product.description}
              imageUrl={product.image}
              variant="full"
              className="mt-4"
            />

          </div>
        </div>

        {/* Linked Blog Articles & Related Guides */}
        {((product.relatedArticleIds && product.relatedArticleIds.length > 0) || (product.relatedArticleLinks && product.relatedArticleLinks.length > 0)) && (() => {
          const linkedArticles = (articles || []).filter(
            (art) =>
              product.relatedArticleIds?.includes(art.id) ||
              product.relatedArticleIds?.includes(art.slug)
          );
          const customLinks = product.relatedArticleLinks || [];

          if (linkedArticles.length === 0 && customLinks.length === 0) return null;

          return (
            <div className="pt-10 border-t border-slate-200 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <BookOpen className="w-4 h-4 text-emerald-700" />
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                      Related Intelligence & Research
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                    {product.relatedArticlesTitle || 'Articles & Technical Insights Related to this Solution'}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    {product.relatedArticlesSubtitle || 'Explore relevant research briefs, implementation guidelines, and case studies for this service.'}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('blog')}
                  className="text-xs font-bold text-slate-600 hover:text-emerald-700 flex items-center gap-1 shrink-0"
                >
                  <span>View All Articles</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Linked Internal Articles */}
                {linkedArticles.map((art) => (
                  <div
                    key={art.id}
                    onClick={() => navigate('article-detail', art.slug || art.id)}
                    className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs hover:shadow-lg transition-all duration-300 flex flex-col group cursor-pointer"
                  >
                    <div className="relative aspect-16/10 bg-slate-100 overflow-hidden">
                      <img
                        src={art.coverImage || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80'}
                        alt={art.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2.5 left-2.5 bg-emerald-800/85 backdrop-blur-xs text-white text-[10px] px-2 py-0.5 rounded-md font-semibold">
                        {art.categoryName || 'Insight'}
                      </div>
                    </div>

                    <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                      <div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 font-medium mb-1.5">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {art.createdAt
                              ? new Date(art.createdAt).toLocaleDateString('en-US', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : 'Recent'}
                          </span>
                        </div>

                        <h4 className="font-bold text-slate-900 text-sm group-hover:text-emerald-700 transition-colors">
                          {art.title}
                        </h4>

                        <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">
                          {art.summary || 'Read complete insights and recommendations...'}
                        </p>
                      </div>

                      <div className="pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-emerald-700">
                        <span>Read Full Article</span>
                        <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Custom External / Internal Links */}
                {customLinks.map((link, idx) => (
                  <a
                    key={`custom-${idx}`}
                    href={link.url}
                    target={link.url.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    className="bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-2xl border border-blue-200/80 p-5 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between group"
                  >
                    <div className="space-y-2">
                      <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
                        <ExternalLink className="w-4 h-4" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 block">
                        Reference Document / Resource
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-700 transition-colors">
                        {link.title}
                      </h4>
                      <p className="text-xs text-slate-500 font-mono truncate">
                        {link.url}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-blue-100 flex items-center justify-between text-xs font-bold text-blue-700">
                      <span>Open Reference Link</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Related Products / Recommended Packages */}
        {product.showRelatedProducts !== false && relatedProducts.length > 0 && (
          <div className="pt-12 border-t border-slate-200 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg sm:text-xl font-extrabold text-slate-900">
                  {product.relatedProductsTitle || 'Alternative Solutions & Services'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {product.relatedProductsSubtitle || 'Explore complementary sustainability services that align with your institutional goals'}
                </p>
              </div>

              <button
                onClick={() => navigate('products')}
                className="text-xs font-bold hover:underline flex items-center gap-1"
                style={{ color: primaryColor }}
              >
                <span>View Full Catalog</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedProducts.map((rel) => (
                <div
                  key={rel.id}
                  onClick={() => navigate('product-detail', rel.slug || rel.id)}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs hover:shadow-lg transition-all duration-300 flex flex-col group cursor-pointer"
                >
                  <div className="relative aspect-16/10 bg-slate-100 overflow-hidden">
                    <img
                      src={rel.image || 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=600&q=80'}
                      alt={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-xs text-white text-[10px] px-2 py-0.5 rounded-md font-semibold">
                      {rel.category}
                    </div>
                  </div>

                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm group-hover:text-emerald-700 transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">
                        {rel.description}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-sm font-extrabold text-slate-900" style={{ color: primaryColor }}>
                        {formatRupiah(rel.price)}
                      </span>
                      <span className="text-xs font-bold text-emerald-700 flex items-center gap-0.5">
                        <span>Details</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Floating Bottom Bar on Mobile for Instant WhatsApp Ordering */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-md border-t border-slate-200 z-40 flex items-center justify-between gap-3 shadow-lg">
        <div>
          <span className="text-[10px] text-slate-400 font-bold block uppercase">
            {product.hidePrice ? 'Inquiry:' : 'Fee Structure:'}
          </span>
          <span className="text-base font-black text-slate-900" style={{ color: primaryColor }}>
            {product.hidePrice ? (product.priceCustomLabel || 'Contact Advisory') : formatRupiah(product.price)}
          </span>
        </div>

        <a
          href={getWhatsAppLink(product)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-2.5 px-4 rounded-xl text-white font-extrabold text-xs shadow-md flex items-center justify-center gap-1.5"
          style={{ backgroundColor: primaryColor }}
        >
          <MessageCircle className="w-4 h-4" />
          <span>Inquire via WhatsApp</span>
        </a>
      </div>
    </div>
  );
}
